import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PackagingMinimisationAssessmentStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { PackagingAssessmentRunDetailSheet } from './PackagingAssessmentRunDetailSheet'
import {
  formatRunTimestamp,
  getMinimisationAssessmentRunSummary,
  PackagingMinimisationAssessmentRunResults,
} from './PackagingMinimisationAssessmentRunResults'

type PackagingMinimisationAssessmentHistoryTableProps = {
  history: PackagingMinimisationAssessmentStoredEntry[]
  locale: string
}

function MinimisationHistorySummary({
  entry,
}: {
  entry: PackagingMinimisationAssessmentStoredEntry
}) {
  const { t } = useTranslation('packagingSuppliers')
  const summary = getMinimisationAssessmentRunSummary(entry)

  const weightLabel =
    summary.weightGrams != null
      ? t('itemDetail.minimisation.weightGrams', { grams: summary.weightGrams })
      : t('itemDetail.minimisation.notDetermined')
  const volumeLabel =
    summary.volumeCm3 != null
      ? t('itemDetail.minimisation.volumeCm3', { volume: summary.volumeCm3 })
      : t('itemDetail.minimisation.notDetermined')

  return (
    <span className='text-sm text-muted-foreground'>
      {t('itemDetail.minimisation.criticalAreasCount', {
        count: summary.criticalAreaCount,
      })}
      {' · '}
      {t('itemDetail.minimisation.minimumWeight', { value: weightLabel })}
      {' · '}
      {t('itemDetail.minimisation.minimumVolume', { value: volumeLabel })}
    </span>
  )
}

export function PackagingMinimisationAssessmentHistoryTable({
  history,
  locale,
}: PackagingMinimisationAssessmentHistoryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(
    null
  )

  const selectedEntry = history.find(
    (entry) => entry.timestamp === selectedTimestamp
  )
  const selectedTitle =
    selectedEntry != null
      ? formatRunTimestamp(selectedEntry.timestamp, locale)
      : ''

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('itemDetail.minimisation.history.columnRun')}
            </TableHead>
            <TableHead>
              {t('itemDetail.minimisation.history.columnVersion')}
            </TableHead>
            <TableHead>
              {t('itemDetail.minimisation.history.columnSummary')}
            </TableHead>
            <TableHead className='w-[1%] text-end'>
              <span className='sr-only'>
                {t('itemDetail.minimisation.history.columnActions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => {
            const runLabel = formatRunTimestamp(entry.timestamp, locale)
            return (
              <TableRow key={entry.timestamp}>
                <TableCell className='font-medium'>{runLabel}</TableCell>
                <TableCell>
                  {t('itemDetail.minimisation.version', {
                    version: entry.assessmentVersion,
                  })}
                </TableCell>
                <TableCell>
                  <MinimisationHistorySummary entry={entry} />
                </TableCell>
                <TableCell className='text-end'>
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto px-0'
                    onClick={() => {
                      setSelectedTimestamp(entry.timestamp)
                    }}
                  >
                    {t('itemDetail.minimisation.history.viewResults')}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <PackagingAssessmentRunDetailSheet
        open={selectedEntry != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTimestamp(null)
          }
        }}
        title={selectedTitle}
      >
        {selectedEntry != null ? (
          <PackagingMinimisationAssessmentRunResults result={selectedEntry} />
        ) : null}
      </PackagingAssessmentRunDetailSheet>
    </>
  )
}
